import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { updateStore } from '../slices/storeSlice';

const EditStoreDialog = ({ store, open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState({
    name: '',
    subdomain: ''
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when store changes
  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name || '',
        subdomain: store.subdomain || ''
      });
      setFormError('');
    }
  }, [store]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.subdomain) {
      setFormError('Store name and subdomain are required');
      return;
    }

    // Basic validation
    if (formData.name.trim().length < 2) {
      setFormError('Store name must be at least 2 characters long');
      return;
    }

    if (formData.subdomain.trim().length < 3) {
      setFormError('Subdomain must be at least 3 characters long');
      return;
    }

    // Removed subdomain format validation - allowing any characters

    setIsSubmitting(true);
    setFormError('');

    try {
      await dispatch(updateStore({
        storeId: store.storeId,
        storeData: {
          name: formData.name.trim(),
          subdomain: formData.subdomain.trim().toLowerCase()
        }
      })).unwrap();

      // Success - close dialog and notify parent
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      setFormError(error.message || 'Failed to update store');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormError('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        Edit Store: {store?.name}
      </DialogTitle>
      
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Store Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
            helperText="Enter a descriptive name for your store"
          />
          
          <TextField
            margin="dense"
            label="Subdomain"
            fullWidth
            variant="outlined"
            value={formData.subdomain}
            onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
            disabled={isSubmitting}
            helperText="This will be used for your store's URL (e.g., mystore.yourdomain.com)"
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            <strong>Note:</strong> Changing the subdomain may affect your store's URL. Make sure to update any bookmarks or links.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isSubmitting || !formData.name.trim() || !formData.subdomain.trim()}
        >
          {isSubmitting ? 'Updating...' : 'Update Store'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditStoreDialog; 